/**
 * Register Form Component
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Input from '../shared/Input';
import Button from '../shared/Button';
import { getSchools, SchoolResponse } from '../../api/schools';

const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    role: 'student' as 'student' | 'teacher',
    school_id: '',
  });

  const [errors, setErrors] = useState<{
    username?: string;
    email?: string;
    full_name?: string;
    password?: string;
    confirmPassword?: string;
    school_id?: string;
    general?: string;
  }>({});

  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState<SchoolResponse[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(true);

  // School search state
  const [schoolQuery, setSchoolQuery] = useState('');
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const schoolInputRef = useRef<HTMLInputElement>(null);
  const schoolDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch schools on mount (public API, no auth needed)
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const data = await getSchools();
        setSchools(data);
        if (data.length === 1) {
          setFormData((prev) => ({ ...prev, school_id: data[0].school_id }));
          setSchoolQuery(data[0].name);
        }
      } catch (err) {
        console.error('Failed to fetch schools:', err);
      } finally {
        setLoadingSchools(false);
      }
    };
    fetchSchools();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        schoolDropdownRef.current &&
        !schoolDropdownRef.current.contains(e.target as Node) &&
        schoolInputRef.current &&
        !schoolInputRef.current.contains(e.target as Node)
      ) {
        setShowSchoolDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtered schools based on search query
  const filteredSchools = useMemo(() => {
    if (!schoolQuery.trim()) return schools;
    const q = schoolQuery.toLowerCase();
    return schools.filter((s) => s.name.toLowerCase().includes(q));
  }, [schools, schoolQuery]);

  const handleSelectSchool = (school: SchoolResponse) => {
    setFormData((prev) => ({ ...prev, school_id: school.school_id }));
    setSchoolQuery(school.name);
    setShowSchoolDropdown(false);
    setErrors((prev) => ({ ...prev, school_id: undefined }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.school_id) {
      newErrors.school_id = 'Please select a school';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await register({
        username: formData.username,
        email: formData.email,
        full_name: formData.full_name,
        password: formData.password,
        role: formData.role,
        school_id: formData.school_id,
      });
      // Redirect handled by AuthContext/App routing
    } catch (error: any) {
      console.error('Registration error:', error);

      // Parse error message from different possible structures
      let errorMessage = 'Registration failed. Please try again.';

      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {errors.general && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm animate-pulse">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
            </svg>
            <span className="font-medium">{errors.general}</span>
          </div>
        </div>
      )}

      <Input
        id="username"
        name="username"
        type="text"
        label="Username"
        placeholder="Choose a username"
        value={formData.username}
        onChange={handleChange}
        error={errors.username}
        fullWidth
        required
        autoComplete="username"
      />

      <Input
        id="email"
        name="email"
        type="email"
        label="Email"
        placeholder="your.email@example.com"
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
        fullWidth
        required
        autoComplete="email"
      />

      <Input
        id="full_name"
        name="full_name"
        type="text"
        label="Full Name"
        placeholder="John Doe"
        value={formData.full_name}
        onChange={handleChange}
        error={errors.full_name}
        fullWidth
        required
        autoComplete="name"
      />

      <Input
        id="password"
        name="password"
        type="password"
        label="Password"
        placeholder="At least 6 characters"
        value={formData.password}
        onChange={handleChange}
        error={errors.password}
        fullWidth
        required
        autoComplete="new-password"
      />

      <Input
        id="confirmPassword"
        name="confirmPassword"
        type="password"
        label="Confirm Password"
        placeholder="Re-enter your password"
        value={formData.confirmPassword}
        onChange={handleChange}
        error={errors.confirmPassword}
        fullWidth
        required
        autoComplete="new-password"
      />

      <div>
        <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          I am a <span className="text-red-500">*</span>
        </label>
        <select
          id="role"
          name="role"
          value={formData.role}
          onChange={handleChange}
          className="block w-full px-4 py-2.5 border border-gray-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
          required
        >
          <option value="student">Student</option>
          <option value="teacher">Teacher</option>
        </select>
      </div>

      <div className="relative">
        <label htmlFor="school_search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          School <span className="text-red-500">*</span>
        </label>
        {loadingSchools ? (
          <div className="flex items-center px-4 py-2.5 border border-gray-300 dark:border-secondary-600 rounded-lg bg-gray-50 dark:bg-secondary-700 text-gray-500 dark:text-secondary-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mr-2"></div>
            Loading schools...
          </div>
        ) : (
          <div className="relative">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={schoolInputRef}
                id="school_search"
                type="text"
                placeholder="Search for your school..."
                value={schoolQuery}
                onChange={(e) => {
                  setSchoolQuery(e.target.value);
                  setShowSchoolDropdown(true);
                  // Clear selection if user edits the text
                  if (formData.school_id) {
                    const selected = schools.find((s) => s.school_id === formData.school_id);
                    if (selected && e.target.value !== selected.name) {
                      setFormData((prev) => ({ ...prev, school_id: '' }));
                    }
                  }
                }}
                onFocus={() => setShowSchoolDropdown(true)}
                className={`block w-full pl-9 pr-8 py-2.5 border rounded-lg bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${
                  errors.school_id ? 'border-red-500' : 'border-gray-300 dark:border-secondary-600'
                }`}
                autoComplete="off"
              />
              {formData.school_id && (
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>

            {/* Dropdown results */}
            {showSchoolDropdown && (
              <div
                ref={schoolDropdownRef}
                className="absolute z-50 w-full mt-1 bg-white dark:bg-secondary-700 border border-gray-200 dark:border-secondary-600 rounded-lg shadow-lg max-h-48 overflow-y-auto"
              >
                {filteredSchools.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    No schools found
                  </div>
                ) : (
                  filteredSchools.map((school) => (
                    <button
                      key={school.school_id}
                      type="button"
                      onClick={() => handleSelectSchool(school)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-primary-50 dark:hover:bg-secondary-600 transition-colors ${
                        formData.school_id === school.school_id
                          ? 'bg-primary-50 dark:bg-secondary-600 text-primary-700 dark:text-primary-400 font-medium'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {school.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
        {errors.school_id && (
          <p className="mt-1 text-sm text-red-500">{errors.school_id}</p>
        )}
      </div>

      <Button type="submit" fullWidth loading={loading}>
        Create Account
      </Button>
    </form>
  );
};

export default RegisterForm;
